import { create } from 'zustand';
import type { SelfUser } from '@comms/shared';
import { ApiError, api, setCsrfToken } from './api';
import { GatewayClient } from './gateway';
import { startE2ee, stopE2ee } from './e2ee';
import { leaveVoice } from './voice';
import { disablePush, registerServiceWorker, syncPush } from './push';
import { startBadge, stopBadge } from './badge';
import { resetStore } from '../store';
import { resetCommands } from '../store/commands';

interface SessionState {
  /** offline: the server couldn't be reached at startup (we retry when back online) */
  status: 'loading' | 'anonymous' | 'authenticated' | 'offline';
  user: SelfUser | null;
  e2eeError: string | null;
}

export const useSession = create<SessionState>(() => ({
  status: 'loading',
  user: null,
  e2eeError: null,
}));

let gateway: GatewayClient | null = null;
export const getGateway = () => gateway;

/** Seconds to wait before each retry of a failed encryption start (the last one repeats). */
const E2EE_RETRY_DELAYS = [5, 15, 30, 60];
let stopE2eeRetry: (() => void) | null = null;

/**
 * Start this device's encryption, retrying temporary failures (rate limits, a flaky
 * connection) until it works. A page without WebCrypto will never work, so that isn't retried.
 */
function startE2eeWithRetry(userId: string) {
  let attempt = 0;
  let timer = 0;
  let stopped = false;
  const retryNow = () => {
    window.clearTimeout(timer);
    void run();
  };
  const stop = () => {
    stopped = true;
    window.clearTimeout(timer);
    window.removeEventListener('online', retryNow);
  };
  const run = async () => {
    try {
      await startE2ee(userId);
      if (stopped) return;
      stop();
      useSession.setState({ e2eeError: null });
    } catch (err) {
      if (stopped) return;
      console.error(err);
      const message = err instanceof Error ? err.message : 'Encryption setup failed';
      if (!globalThis.isSecureContext || !globalThis.crypto?.subtle) {
        stop();
        useSession.setState({ e2eeError: message });
        return;
      }
      const delay = E2EE_RETRY_DELAYS[Math.min(attempt++, E2EE_RETRY_DELAYS.length - 1)]!;
      useSession.setState({ e2eeError: `${message} (retrying in ${delay}s)` });
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void run(), delay * 1000);
    }
  };
  window.addEventListener('online', retryNow);
  void run();
  return stop;
}

function onAuthLost() {
  void leaveVoice();
  // Don't leave a logged-out browser subscribed (the server already dropped it with the session).
  void disablePush({ server: false });
  gateway?.disconnect();
  gateway = null;
  stopE2eeRetry?.();
  stopE2eeRetry = null;
  stopE2ee();
  stopBadge();
  setCsrfToken(null);
  resetStore();
  resetCommands();
  useSession.setState({ status: 'anonymous', user: null });
}

/**
 * Checks with the server whether this browser is still signed in, refreshing the CSRF
 * token if so. Null when the server can't be reached (offline, restarting).
 */
async function stillSignedIn(): Promise<boolean | null> {
  try {
    const s = await api<{ user: SelfUser; csrfToken: string }>('GET', '/auth/session');
    setCsrfToken(s.csrfToken);
    useSession.setState({ user: s.user });
    return true;
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) return false;
    return null;
  }
}

export async function startSession(user: SelfUser, csrfToken: string) {
  setCsrfToken(csrfToken);
  useSession.setState({ status: 'authenticated', user, e2eeError: null });
  gateway = new GatewayClient(onAuthLost, stillSignedIn);
  let started = false;
  gateway.on((t) => {
    if (t === 'READY' && !started) {
      started = true;
      stopE2eeRetry?.();
      stopE2eeRetry = startE2eeWithRetry(user.id);
    }
  });
  gateway.connect();
  startBadge();
  registerServiceWorker()?.catch(() => {});
  void syncPush();
}

export async function restoreSession() {
  try {
    const s = await api<{ user: SelfUser; csrfToken: string }>('GET', '/auth/session');
    await startSession(s.user, s.csrfToken);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      useSession.setState({ status: 'anonymous' });
      return;
    }
    // No answer (offline, the server restarting) or a temporary error such as a rate
    // limit: don't show the login page, wait and try again.
    useSession.setState({ status: 'offline' });
    const retry = () => {
      window.removeEventListener('online', retry);
      window.clearTimeout(timer);
      void restoreSession();
    };
    window.addEventListener('online', retry);
    const timer = window.setTimeout(retry, 10_000);
  }
}

export async function logout() {
  await api('POST', '/auth/logout').catch(() => {});
  onAuthLost();
}
